import React, { useState, useRef, useEffect } from "react"
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  Animated,
  Platform
} from "react-native"

import Modal from "modal-enhanced-react-native-web"
import { Ionicons } from "@expo/vector-icons"
import ExistingFlowsStep from "./ExistingFlowsStep"
import { PropertySelectionStep } from "./PropertySelectionStep"
import IssueFormStep from "./IssueFormStep"
import { addIssue } from "../../redux/action/maintenance.actions"
import { useDispatch } from "react-redux"
import { COLORS } from "../../screens/tenants/tenantOnboarding/screens/components/PropertyDetailsCard"

const { width } = Dimensions.get("window")

const steps = [
  "Select Property",
  "Existing Flow",
  "Issue Details"
]

interface Props {
  open: boolean
  onClose: () => void
  onIssueAdded: () => void,
  refresh: () => void,
}

export default function AddNewIssueModal({
  open,
  onClose,
  onIssueAdded,
  refresh
}: Props) {

  const dispatch = useDispatch()

  const [activeStep, setActiveStep] = useState(0)
  const [payload, setPayload] = useState<any>({})

  const progressWidth = ((activeStep + 1) / steps.length) * 100

  const fadeAnim = useRef(new Animated.Value(0)).current

  useEffect(() => {
    fadeAnim.setValue(0)
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 300,
      useNativeDriver: true
    }).start()
  }, [activeStep])

  const handleClose = () => {
    setActiveStep(0)
    setPayload({})
    onClose()
  }

  const handleBack = () => {
    if (activeStep > 0) setActiveStep(prev => prev - 1)
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <PropertySelectionStep
            onNext={({ selectedProperty, existingFlows }) => {
              setPayload({
                ...payload,
                selectedProperty,
                existingFlows
              })
              setActiveStep(1)
            }}
            onCancel={handleClose}
          />
        )

      case 1:
        return (
          <ExistingFlowsStep
            selectedProperty={payload.selectedProperty}
            existingFlows={payload.existingFlows}
            onBack={handleBack}
            onNext={(flowSummary) => {
              setPayload({
                ...payload,
                selectedFlowSummary: flowSummary
              })
              setActiveStep(2)
            }}
          />
        )

      case 2:
        return (
          <IssueFormStep
            selectedFlowSummary={payload.selectedFlowSummary}
            onBack={handleBack}
            onSubmit={(data) => {

              const payloadToSend = {
                title: data.title,
                date_opened: new Date(data.date_opened).toISOString(),
                videos: [],
                images: data.issue_images,
                description: data.description,
                category_id: Number(data.category_id),
                room_type_id: Number(data.room_type_id),
                openflow_id: payload?.selectedFlowSummary?.flowId
              }

              dispatch(addIssue(payloadToSend, () => {
                onIssueAdded()
                handleClose()
              }))
            }}
          />
        )

      default:
        return null
    }
  }

  return (
    <Modal
      isVisible={open}
      backdropTransitionInTiming={200}
      backdropTransitionOutTiming={200}
      useNativeDriver={false}
      style={{ margin: 0 }}
    >
      <View style={styles.overlay}>
        <View style={styles.container}>

          {/* HEADER */}
          <View style={styles.header}>
            <Text style={styles.title}>
              Create Maintenance Issue
            </Text>

            <TouchableOpacity onPress={handleClose}>
              <Ionicons name="close" size={24} color={COLORS.primary} />
            </TouchableOpacity>
          </View>

          {/* STEPPER */}
          <View style={styles.progressWrapper}>

            <View style={styles.progressBackground}>
              <View
                style={[
                  styles.progressActive,
                  { width: `${progressWidth}%` }
                ]}
              />
            </View>

            <View style={styles.stepsRow}>
              {steps.map((label, index) => {
                const isActive = index === activeStep
                const isCompleted = index < activeStep

                return (
                  <View key={index} style={styles.stepItem}>
                    <View
                      style={[
                        styles.circle,
                        isCompleted && styles.circleCompleted,
                        isActive && styles.circleActive
                      ]}
                    >
                      {isCompleted ? (
                        <Ionicons name="checkmark" size={16} color="#fff" />
                      ) : (
                        <Text style={styles.circleText}>
                          {index + 1}
                        </Text>
                      )}
                    </View>

                    <Text
                      style={[
                        styles.stepLabel,
                        isActive && styles.stepLabelActive
                      ]}
                    >
                      {label}
                    </Text>
                  </View>
                )
              })}
            </View>

          </View>

          {/* CONTENT */}
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
          >
            <Animated.View style={{ opacity: fadeAnim }}>
              {renderStepContent()}
            </Animated.View>
          </ScrollView>

        </View>
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({

  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.4)"
  },

  container: {
    width: width > 1000 ? 950 : "95%",
    height: width > 1000 ? 680 : "88%",
    backgroundColor: "#fff",
    borderRadius: 22,
    overflow: "hidden",
    ...Platform.select({
      web: {
        boxShadow: "0 20px 50px rgba(109, 8, 192, 0.15)"
      },
      default: {
        elevation: 10
      }
    })
  },

  header: {
    padding: 22,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderColor: "#F3F4F6"
  },

  title: {
    fontSize: 20,
    fontWeight: "700",
    color: COLORS.primary
  },

  progressWrapper: {
    paddingHorizontal: 30,
    paddingTop: 25,
    paddingBottom: 10
  },

  progressBackground: {
    height: 8,
    backgroundColor: "#F1E6FB",
    borderRadius: 10,
    overflow: "hidden",
    marginBottom: 25
  },

  progressActive: {
    height: "100%",
    backgroundColor:COLORS.primary,
    borderRadius: 10
  },

  stepsRow: {
    flexDirection: "row",
    justifyContent: "space-between"
  },

  stepItem: {
    alignItems: "center",
    flex: 1
  },

  circle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#E9D8F7",
    justifyContent: "center",
    alignItems: "center"
  },

  circleActive: {
    backgroundColor: "#6D08C0",
    shadowColor: "#6D08C0",
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 5
  },

  circleCompleted: {
    backgroundColor:COLORS.primary
  },

  circleText: {
    color: "#fff",
    fontWeight: "700"
  },

  stepLabel: {
    marginTop: 8,
    fontSize: 12,
    color: "#9CA3AF",
    textAlign: "center"
  },

  stepLabelActive: {
    color: COLORS.primary,
    fontWeight: "700"
  },

  content: {
    padding: 10,
    paddingBottom: 40
  }

})
